
import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { cleanupUserAvatars } from '@/utils/storage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from '@/components/ui/textarea';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { glassCardStatic } from '@/utils/admin-glass-styles';

const AvatarCleanupTool = () => {
  const { toast } = useToast();
  const [userId, setUserId] = useState('');
  const [bucketName, setBucketName] = useState<'client.photo' | 'practitioner.photo'>('client.photo');
  const [processing, setProcessing] = useState(false);
  const [logMessages, setLogMessages] = useState<string[]>([]);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const addLogMessage = (message: string) => {
    setLogMessages(prev => [...prev, `[${new Date().toISOString()}] ${message}`]);
  };

  const handleCleanup = async () => {
    if (!userId.trim()) {
      toast({
        title: "Error",
        description: "Please enter a valid user ID",
        variant: "destructive",
      });
      return;
    }

    setProcessing(true);
    setStatus('idle');
    setLogMessages([]);
    addLogMessage(`Starting cleanup of avatar files for user ${userId} in bucket ${bucketName}`);

    try {
      // List all files in user folder
      addLogMessage(`Listing all files in user folder ${userId}...`);
      const { data: fileList, error: listError } = await supabase.storage
        .from(bucketName)
        .list(userId);

      if (listError) {
        throw listError;
      }

      addLogMessage(`Found ${fileList?.length || 0} files`);

      // Clean up user avatars
      await cleanupUserAvatars(bucketName, userId);
      
      // Confirm cleanup
      const { data: afterCleanup, error: afterError } = await supabase.storage
        .from(bucketName)
        .list(userId);
        
      if (afterError) {
        throw afterError;
      }
      
      addLogMessage(`Cleanup completed. Folder now contains ${afterCleanup?.length || 0} files`);
      setStatus('success');
      
      toast({
        title: "Cleanup Successful",
        description: `Successfully cleaned up avatar files for user ${userId}`,
      });
    } catch (error: any) {
      console.error('Avatar cleanup error:', error);
      addLogMessage(`Error: ${error.message || 'Unknown error'}`);
      setStatus('error');
      
      toast({
        title: "Cleanup Failed",
        description: error.message || "An error occurred while cleaning up avatars",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card className={glassCardStatic}>
      <CardHeader>
        <CardTitle>Avatar File Cleanup Tool</CardTitle>
        <CardDescription>Clean up problematic avatar files in user storage buckets</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="user-id">User ID</Label>
          <Input 
            id="user-id" 
            placeholder="Enter user UUID" 
            value={userId} 
            onChange={e => setUserId(e.target.value)} 
            disabled={processing}
          />
        </div>
        
        <div className="space-y-2">
          <Label>Storage Bucket</Label>
          <Select 
            disabled={processing}
            value={bucketName}
            onValueChange={(value: 'client.photo' | 'practitioner.photo') => setBucketName(value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select storage bucket" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="client.photo">Client Photos (client.photo)</SelectItem>
              <SelectItem value="practitioner.photo">Practitioner Photos (practitioner.photo)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Button 
            onClick={handleCleanup} 
            disabled={processing || !userId.trim()} 
            className="w-full"
          >
            {processing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Clean Avatar Files'
            )}
          </Button>
        </div>
        
        {logMessages.length > 0 && (
          <div className="space-y-2 mt-4">
            <Label className="flex items-center gap-2">
              Processing Log
              {status === 'success' && <CheckCircle className="h-4 w-4 text-green-500" />}
              {status === 'error' && <AlertCircle className="h-4 w-4 text-red-500" />}
            </Label>
            <Textarea 
              value={logMessages.join('\n')}
              readOnly
              className="font-mono text-sm h-32"
            />
          </div>
        )}
      </CardContent>
      <CardFooter className="text-sm text-gray-500">
        This tool will clean up all files in the user folder, ensuring proper avatar naming and storage.
      </CardFooter>
    </Card>
  );
};

export default AvatarCleanupTool;
